/**
 * PublishBanner - Shows pending changes with countdown timer
 *
 * Displays at the top of the admin dashboard when there are
 * unpublished changes. Shows countdown to auto-publish with
 * options to publish now or cancel.
 */
import { useState, useEffect } from 'react';
import { AlertCircle, Clock, Upload, X, ChevronDown, ChevronUp } from 'lucide-react';

import { Button } from '../ui/button';
import usePublishStatus from '../../hooks/usePublishStatus';

const PublishBanner = () => {
  const {
    hasPendingChanges,
    scheduledPublishAt,
    pendingChanges,
    publishing,
    triggerPublish,
    cancelPublish,
  } = usePublishStatus();

  const [timeLeft, setTimeLeft] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (!scheduledPublishAt) {
      setTimeLeft(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = scheduledPublishAt - now;

      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [scheduledPublishAt]);

  const handlePublish = async () => {
    try {
      await triggerPublish();
    } catch (err) {
      console.error('Publish failed:', err);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelPublish();
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  };

  // Don't show if no pending changes
  if (!hasPendingChanges) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-3">
          {/* Main banner row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <div>
                <span className="font-medium">
                  {pendingChanges.length} unpublished change{pendingChanges.length !== 1 ? 's' : ''}
                </span>
                {timeLeft && (
                  <span className="ml-2 text-amber-100">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Auto-publish in {timeLeft.minutes}:{timeLeft.seconds.toString().padStart(2, '0')}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setExpanded(!expanded)}
                className="bg-background/20 hover:bg-background/30 text-white border-0"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Details
                  </>
                )}
              </Button>

              <Button
                size="sm"
                variant="secondary"
                onClick={handleCancel}
                disabled={publishing}
                className="bg-background/20 hover:bg-background/30 text-white border-0"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>

              <Button
                size="sm"
                onClick={handlePublish}
                disabled={publishing}
                className="bg-background text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30"
              >
                <Upload className="h-4 w-4 mr-1" />
                {publishing ? 'Publishing...' : 'Publish Now'}
              </Button>
            </div>
          </div>

          {/* Expanded details */}
          {expanded && pendingChanges.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-400/50">
              <p className="text-sm text-amber-100 mb-2">Pending changes:</p>
              <ul className="text-sm space-y-1">
                {pendingChanges.map((change, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-background rounded-full" />
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishBanner;
